import { useEffect, useState } from "react";

import type { AnchorRiskResponse } from "@exploitlens/ui-contracts";

import { fetchAnchorRisk } from "../../lib/anchor";

type AnchorState =
  | { status: "loading" }
  | { status: "available"; response: AnchorRiskResponse }
  | { status: "unavailable"; message: string };

function number(value: number | null): string {
  if (value === null) return "Not available";
  return value.toLocaleString("en-US", { maximumFractionDigits: 6 });
}

function percentage(value: number | null): string {
  return value === null ? "Not available" : `${number(value)}%`;
}

function usd(value: number): string {
  return value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded border border-rule bg-canvas px-3 py-2">
      <dt className="text-xs uppercase tracking-wide text-ink-faint">{label}</dt>
      <dd className="mt-1 font-evidence text-sm text-ink">{value}</dd>
    </div>
  );
}

/**
 * Live, non-authoritative Anchor context. Its state is intentionally local
 * to this card: a failure cannot affect the loaded bundle, receipt, gates,
 * replay, variants, causal divergence, or verifier result around it.
 */
export function AnchorRiskIntelligence({ wallet }: { wallet: `0x${string}` }) {
  const [state, setState] = useState<AnchorState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    fetchAnchorRisk(wallet)
      .then((response) => {
        if (!cancelled) setState({ status: "available", response });
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setState({
          status: "unavailable",
          message: cause instanceof Error ? cause.message : String(cause),
        });
      });
    return () => {
      cancelled = true;
    };
  }, [wallet]);

  return (
    <div className="rounded border border-rule bg-canvas-raised p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-sm font-medium text-ink">Anchor counterparty-risk intelligence</h3>
        <span className="rounded-full bg-canvas px-2 py-0.5 text-xs uppercase tracking-wide text-ink-faint">
          Not part of the verdict
        </span>
      </div>
      <p className="mt-2 text-sm text-ink-muted">
        Current Anchor counterparty-risk context for reviewed wallet{" "}
        <code className="font-evidence text-xs text-ink">{wallet}</code>. It does not prove
        historical maliciousness and cannot revise the deterministic ExploitLens verdict.
      </p>

      {state.status === "loading" && (
        <p className="mt-4 text-sm text-ink-muted" role="status">
          Loading current Anchor context...
        </p>
      )}

      {state.status === "unavailable" && (
        <div className="mt-4 rounded border border-rule bg-canvas px-3 py-3" role="status">
          <p className="text-sm font-medium text-ink">Anchor unavailable</p>
          <p className="mt-1 text-sm text-ink-muted">{state.message}</p>
          <p className="mt-1 text-xs text-ink-faint">
            The deterministic verdict and Telegraph transaction intelligence remain unchanged.
          </p>
        </div>
      )}

      {state.status === "available" && (
        <div className="mt-4 space-y-4">
          <div className="flex flex-wrap items-baseline justify-between gap-3 rounded border border-rule bg-canvas px-3 py-3">
            <div>
              <p className="text-xs uppercase tracking-wide text-ink-faint">Anchor verdict</p>
              <p className="mt-1 font-evidence text-lg font-semibold text-ink">
                {state.response.verdict}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-wide text-ink-faint">Risk label</p>
              <p className="mt-1 font-evidence text-sm text-ink">
                {state.response.signals.riskLabel}
              </p>
            </div>
          </div>

          <p className="text-sm text-ink">{state.response.reasoning}</p>

          <dl className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            <Metric label="Health factor" value={number(state.response.signals.healthFactor)} />
            <Metric
              label="Liquidation distance"
              value={percentage(state.response.signals.liquidationDistancePercent)}
            />
            <Metric
              label="Total collateral"
              value={usd(state.response.signals.totalCollateralUSD)}
            />
            <Metric label="Total debt" value={usd(state.response.signals.totalDebtUSD)} />
            <Metric
              label="Liquidation threshold"
              value={number(state.response.signals.liquidationThreshold)}
            />
            <Metric label="Confidence" value={number(state.response.confidence)} />
          </dl>

          <p className="font-evidence text-xs text-ink-faint">
            Block {state.response.meta.blockNumber.toLocaleString("en-US")} ·{" "}
            {state.response.meta.network} · chain {state.response.meta.chainId} ·{" "}
            {state.response.protocol} · {state.response.meta.source} ·{" "}
            {state.response.meta.timestamp}
          </p>
        </div>
      )}
    </div>
  );
}
